/**
 * Shared UI primitives for the ops console. Reused across Coverage, Clubs, Runs, etc.
 */

import React from "react";

/**
 * v2-style page header (eyebrow + title + sub). Use once per view for the main heading.
 * @param {string} label - Section label (eyebrow)
 * @param {string} title - Page title
 * @param {string} [sub] - Optional subtitle
 */
export function ConsolePageHeader({ label, title, sub }) {
  return (
    <>
      <div className="section-label">{label}</div>
      <div className="section-title">{title}</div>
      {sub != null && sub !== "" && <div className="section-sub">{sub}</div>}
    </>
  );
}

/** Inline help icon with tooltip (title). Use for section labels and form fields. */
export function HelpIcon({ tip }) {
  return (
    <span className="console-help-icon" title={tip} aria-label="Help">
      &#8505;
    </span>
  );
}

/**
 * Section label with optional help tooltip.
 * @param {string} label - Label text
 * @param {string} [tip] - Optional tooltip for HelpIcon
 */
export function SectionLabel({ label, tip }) {
  return (
    <div className="console-section-label">
      {label}
      {tip != null && tip !== "" && <HelpIcon tip={tip} />}
    </div>
  );
}

/**
 * Card with kicker and optional help tip. Use for operator tasks, integrity, fragile slots, etc.
 */
export function OpsCard({ kicker, tip, children }) {
  return (
    <div className="console-ops-card">
      <div className="console-ops-kicker">
        {kicker}
        {tip != null && tip !== "" && <HelpIcon tip={tip} />}
      </div>
      {children}
    </div>
  );
}

/** Single row in an ops list (title + description). */
export function OpsRow({ title, description, empty }) {
  return (
    <li className={`console-ops-row ${empty ? "console-ops-empty" : ""}`}>
      {!empty && <strong>{title}</strong>}
      <span>{description}</span>
    </li>
  );
}

/**
 * Renders a list of ops rows or a single empty-state row.
 * @param {Array<{ title: string, description: string }>} [items]
 * @param {string} [emptyMessage]
 */
export function OpsList({ items, emptyMessage }) {
  const hasItems = Array.isArray(items) && items.length > 0;
  return (
    <ul className="console-ops-list">
      {hasItems
        ? items.map((item, i) => (
            <OpsRow
              key={i}
              title={item.title}
              description={item.description}
            />
          ))
        : items && items.length === 0
          ? (
              <OpsRow description={emptyMessage} empty />
            )
          : null}
    </ul>
  );
}

/** One gap row (empty or fragile slot). state must be "empty" or "fragile" from API. */
export function GapRow({ day, slot, state }) {
  const isMissing = state === "empty";
  const isFragile = state === "fragile";
  const slotLabel = slot != null ? String(slot).toUpperCase() : "";
  return (
    <div className={`console-gap-row ${isMissing ? "missing" : isFragile ? "fragile" : ""}`}>
      <span className={`console-gap-dot ${isMissing ? "missing" : isFragile ? "fragile" : ""}`} />
      <span className="console-gap-slot">{day} {slotLabel}</span>
      <span className={`console-gap-tag ${isMissing ? "missing" : isFragile ? "fragile" : ""}`}>
        {isMissing ? "No runs" : isFragile ? "1 run" : ""}
      </span>
    </div>
  );
}
